import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import LandingPage from '@/pages/LandingPage';
import Wordmark from '@/components/Wordmark';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== password2) {
      toast.error('Adgangskoderne matcher ikke');
      return;
    }
    if (password.length < 8) {
      toast.error('Adgangskoden skal være mindst 8 tegn');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Linket er ugyldigt eller udløbet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <div className="fixed inset-0 overflow-auto blur-sm brightness-50 pointer-events-none">
        <LandingPage />
      </div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[var(--surface-overlay)]" onClick={() => navigate('/')} />
        <Card className="relative z-10 w-full max-w-md p-8 bg-th-sidebar border-th-border shadow-2xl shadow-brand-500/10">
          <div className="flex justify-center mb-6">
            <a href="/" className="flex items-center gap-3">
              <Lock size={40} className="text-brand-500" />
              <span className="text-2xl font-heading font-bold text-th-text">
                <Wordmark />
              </span>
            </a>
          </div>

          {done ? (
            <div className="text-center space-y-4">
              <h1 className="text-2xl font-heading font-bold text-th-text">Adgangskode opdateret</h1>
              <p className="text-th-text-secondary">Du kan nu logge ind med din nye adgangskode.</p>
              <Button className="w-full h-12 bg-btn hover:bg-btn-hover text-btn-fg" onClick={() => navigate('/login')}>
                Gå til login
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-heading font-bold text-th-text text-center mb-2">
                Ny adgangskode
              </h1>
              <p className="text-th-text-secondary text-center mb-8">Vælg en ny adgangskode på mindst 8 tegn.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Ny adgangskode"
                    required
                    autoFocus
                    className="pl-10 h-12 bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted"
                  />
                </div>
                <div className="relative">
                  <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
                  <Input
                    type="password"
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                    placeholder="Gentag adgangskode"
                    required
                    className="pl-10 h-12 bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted"
                  />
                </div>
                <Button type="submit" disabled={loading || !token} className="w-full h-12 bg-btn hover:bg-btn-hover text-btn-fg text-lg">
                  {loading ? 'Gemmer...' : 'Gem ny adgangskode'}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
